import { cn, extractVariables } from "@/lib/utils";
import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extension-placeholder";
import VariableTiptapMark from "./tiptap/VariableTiptapMark";

export type RichInputProps = {
  id: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  onVariablesChange?: (id: string, variables: string[]) => void;
};

const RichInput = ({
  id,
  value,
  onChange,
  placeholder,
  className,
  onVariablesChange,
}: RichInputProps) => {
  const editor = useEditor({
    enableInputRules: true,
    enablePasteRules: true,
    content: value,
    extensions: [
      StarterKit.configure({
        bold: false,
        italic: false,
        strike: false,
        code: false,
      }),
      Placeholder.configure({
        placeholder: placeholder || "Enter text",
      }),
      VariableTiptapMark,
    ],
    injectCSS: false,
    onUpdate: ({ editor }) => {
      const text = editor.getJSON().content?.reduce((acc, node) => {
        if (node.type === "paragraph") {
          return (
            acc +
            (node.content?.reduce((text, child) => {
              if (child.marks?.some((mark) => mark.type === "variable")) {
                return text + `{{${child.text}}}`;
              }
              return text + (child.text || "");
            }, "") || "")
          );
        }
        return acc;
      }, "");

      const variables = extractVariables(text ?? "");
      onChange?.(text || "");
      onVariablesChange?.(id, variables);
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm focus:outline-none max-w-none",
      },
    },
  });

  useEffect(() => {
    editor?.chain().focus("end").run();
  }, [editor]);

  return (
    <EditorContent
      editor={editor}
      className={cn(
        "flex w-full rounded-md border border-input bg-transparent p-1.5 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "text-foreground cursor-text",
        "[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[1.5rem] [&_.ProseMirror]:w-full [&_.ProseMirror]:h-full",
        "[&_.ProseMirror_p]:m-0 [&_.ProseMirror_p]:leading-normal",
        className,
      )}
    />
  );
};

export default RichInput;
